import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DealerPrice,
  DealerPriceBlock,
  DoublePrice,
  InfoIconWrapper,
  LegalCTA,
  MSRPBlock,
  MSRPPrice,
  PriceBlock,
  PriceTitle,
} from './VehicleCard.styles';
import InfoIcon from '../../assets/icons/Info';

interface PricesTypes {
  modelPrice: string;
  modelPriceRetail: string;
  vehicleType: string;
  dealerRegion: string;
  isAvailableNow: boolean | null;
  marketType: string;
  onModalOpen: (e: any, title?: string, text?: string) => void;
}

// This component is used only for Tier 1
const Prices: React.FC<PricesTypes> = ({
  modelPrice,
  modelPriceRetail,
  vehicleType,
  isAvailableNow,
  marketType,
  onModalOpen,
}) => {
  const { t } = useTranslation();
  const LegalButtonRef = useRef<HTMLButtonElement | null>(null);

  const isMarketUSA = marketType === 'USA';

  const priceLabel = useMemo(() => {
    if (marketType === 'CAN' && vehicleType === 'new' && !isAvailableNow)
      return t('vlp.vehicleResults.vehicleCard.dealerListPrice');

    if (marketType === 'CAN' && vehicleType === 'new' && isAvailableNow)
      return t('vlp.vehicleResults.vehicleCard.dealerListPrice');

    if (marketType === 'CAN' && vehicleType === 'used')
      return t('vlp.vehicleResults.vehicleCard.dealerListPrice');

    if (marketType === 'USA' && vehicleType === 'new')
      return t('vlp.vehicleResults.vehicleCard.msrpDestinationFeeUSA');

    // marketType === 'USA' && vehicleType === 'used'
    return t('vlp.vehicleResults.vehicleCard.dealerPrice');
  }, [vehicleType, isAvailableNow, t, marketType]);

  const price = useMemo(() => {
    if (isMarketUSA && vehicleType === 'new' && !isAvailableNow) {
      return 'Available soon';
    }
    return `$${modelPrice}`;
  }, [isAvailableNow, isMarketUSA, modelPrice, vehicleType]);

  const retailPrice = useMemo(() => {
    return (
      <DealerPriceBlock isDoublePrice={false}>
        <PriceTitle>{t('vlp.vehicleResults.vehicleCard.dealerPrice')}</PriceTitle>
        <DealerPrice data-testid="model-price">
          {modelPriceRetail}
          <LegalCTA
            ref={LegalButtonRef}
            onClick={onModalOpen}
            aria-label={t('vlp.vehicleResults.vehicleCard.legalInformation')}
            data-component="result-tile-info"
            data-testid="price-legal-popover-trigger"
          >
            <InfoIconWrapper>
              <InfoIcon size={20} />
            </InfoIconWrapper>
          </LegalCTA>
        </DealerPrice>
      </DealerPriceBlock>
    );
  }, [modelPriceRetail, onModalOpen, t]);

  const regularPrice = useMemo(() => {
    return (
      <MSRPBlock isDoublePrice={false}>
        <PriceTitle>{priceLabel}</PriceTitle>
        <MSRPPrice mainPrice={false} data-testid="model-price">
          {price}
          <LegalCTA
            ref={LegalButtonRef}
            onClick={onModalOpen}
            aria-label={t('vlp.vehicleResults.vehicleCard.legalInformation')}
            data-component="result-tile-info"
            data-testid="price-legal-popover-trigger"
          >
            <InfoIconWrapper>
              <InfoIcon size={20} />
            </InfoIconWrapper>
          </LegalCTA>
        </MSRPPrice>
      </MSRPBlock>
    );
  }, [onModalOpen, price, priceLabel, t]);

  const pricesToRender = useMemo(() => {
    if (!isMarketUSA) return retailPrice;
    return regularPrice;
  }, [isMarketUSA, regularPrice, retailPrice]);

  return <PriceBlock>{pricesToRender}</PriceBlock>;
};

export default Prices;
